/**
 * Speech-Synchronized Breathing Animation
 * Adds natural breath patterns before and during speaking
 * Pre-speech inhale, gradual exhale during speech
 * Nuamedia Solutions Pty Ltd
 */

import * as THREE from 'three';

export interface SpeechBreathingState {
  isInhaling: boolean;
  isExhaling: boolean;
  breathIntensity: number;
}

/**
 * Create breathing animation for speech
 * @param utteranceDuration - How long the utterance will be (in seconds)
 * @param morphTargetDictionary - Avatar's morph target dictionary
 * @returns Animation clip with breathing pattern
 */
export function createSpeechBreathingAnimation(
  utteranceDuration: number,
  morphTargetDictionary: Record<string, number>
): { 
  inhaleClip: THREE.AnimationClip | null;
  exhaleClip: THREE.AnimationClip | null;
} {
  
  // Pre-speech inhale (200ms)
  const inhaleFrames = [
    {
      time: 0,
      blendshapes: {
        // Chest expansion - use existing breathing blendshapes
        // These are body deformation for chest/diaphragm
      }
    },
    {
      time: 0.2,  // 200ms inhale
      blendshapes: {
        // Peak inhale - chest expanded
      }
    }
  ];
  
  // Exhale during speech (gradual)
  const exhaleFrames = [
    {
      time: 0,
      blendshapes: {
        // Start from expanded chest
      }
    },
    {
      time: utteranceDuration,
      blendshapes: {
        // Gradually compressed - air released during speech
      }
    }
  ];
  
  // For now, return null - need to identify correct breathing blendshapes in model
  // This will be implemented after checking morphTargetDictionary for breathing controls
  return {
    inhaleClip: null,
    exhaleClip: null
  };
}

/**
 * Detect pauses in blend data where avatar should take a breath
 * @param blendData - Viseme blend data with timestamps
 * @returns Array of pause timestamps (in seconds)
 */
export function detectBreathPauses(
  blendData: Array<{ time?: number; blendshapes: any }>
): number[] {
  
  const pauses: number[] = [];
  const PAUSE_THRESHOLD = 0.3;  // 300ms pause triggers breath
  
  for (let i = 1; i < blendData.length; i++) {
    const prevTime = blendData[i - 1].time || 0;
    const currentTime = blendData[i].time || 0;
    const gap = currentTime - prevTime;
    
    if (gap > PAUSE_THRESHOLD) {
      // Pause detected - add breath at midpoint
      pauses.push(prevTime + gap / 2);
    }
  }
  
  return pauses;
}

/**
 * Overlay a subtle breath cycle onto viseme blend data.
 * Detects pauses (>300ms with little mouth movement) and inserts a small
 * inhale/exhale on jawOpen + cheekPuff without replacing lip-sync.
 */
export function overlayBreathingOnBlendData<T extends { time?: number; blendshapes?: Record<string, number> }>(
  blendData: T[] | null | undefined
): T[] {
  if (!blendData || blendData.length === 0) {
    return blendData ?? [];
  }

  const frames = blendData.map((frame) => ({
    ...frame,
    blendshapes: { ...(frame.blendshapes || {}) },
  }));

  const inhale = (frame: T & { blendshapes: Record<string, number> }, amount: number) => {
    frame.blendshapes.jawOpen = Math.min(1, (frame.blendshapes.jawOpen ?? 0) + 0.03 * amount);
    frame.blendshapes.mouthOpen = Math.min(1, (frame.blendshapes.mouthOpen ?? 0) + 0.02 * amount);
    frame.blendshapes.cheekPuff = Math.min(1, (frame.blendshapes.cheekPuff ?? 0) + 0.015 * amount);
  };

  // Pre-speech inhale on the first ~200ms of frames.
  const firstTime = frames[0].time ?? 0;
  for (const frame of frames) {
    const t = (frame.time ?? 0) - firstTime;
    if (t <= 0.2) {
      inhale(frame, Math.sin((t / 0.2) * Math.PI / 2));
    }
  }

  const pauses = detectBreathPauses(frames);
  for (const pauseTime of pauses) {
    for (const frame of frames) {
      const t = frame.time ?? 0;
      const dist = Math.abs(t - pauseTime);
      if (dist <= 0.25) {
        inhale(frame, 1 - dist / 0.25);
      }
    }
  }

  return frames;
}

/**
 * Add breathing overlay to existing speech animation
 * Uses additive blending to avoid interfering with lip-sync
 */
export function addBreathingToSpeech(
  speechDuration: number,
  startCallback?: () => void,
  endCallback?: () => void
): {
  start: () => void;
  stop: () => void;
} {
  let animationFrameId: number | null = null;
  let startTime = 0;
  let phase: 'inhale' | 'exhale' | 'idle' = 'idle';

  const animate = () => {
    const elapsed = (Date.now() - startTime) / 1000;

    if (phase === 'inhale' && elapsed >= 0.2) {
      phase = 'exhale';
      if (startCallback) startCallback();
    }

    if (elapsed < speechDuration + 0.2) {
      animationFrameId = requestAnimationFrame(animate);
    } else {
      phase = 'idle';
      if (endCallback) endCallback();
    }
  };

  return {
    start: () => {
      startTime = Date.now();
      phase = 'inhale';
      animate();
    },
    stop: () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
      phase = 'idle';
    }
  };
}

